<template>
    <ModalComponent
        :name="'renovacion-membresia-sga-modal'"
        :title="$t('perfil_sga.renovacion_membresia_titulo', { simpatizante: textoSimpatizante })"
        @close="closeModal"
        @closed="onModalClosed"
    >
        <!-- Modal Body (default slot) -->
        <!-- Error state -->
        <b-alert v-if="error" variant="danger" show>
            {{ error }}
        </b-alert>

        <!-- BLOCKED STATE: renewal disabled for this membership (status.value === 2) -->
        <div v-if="status?.value === 2" class="tw:text-center tw:py-8 tw:px-4">
            <div class="mb-3 d-inline-flex align-items-center justify-content-center"
                 style="width: 64px; height: 64px; border-radius: 50%; background-color: #f1f3f5;"
            >
                <b-icon icon="lock-fill" font-scale="2.4" style="color: #adb5bd;" />
            </div>
            <h5 class="mb-2">{{ $t('perfil_sga.renovacion_membresia_bloqueada_titulo', { simpatizante: textoSimpatizante }) }}</h5>
            <p class="text-muted mb-0 px-4">
                {{ $t('perfil_sga.renovacion_membresia_bloqueada_texto', { simpatizante: textoSimpatizante }) }}
            </p>
        </div>

        <!-- NO RENEWAL STATE: can_renew is false and not blocked -->
        <div v-else-if="!can_renew" class="tw:text-center tw:py-8 tw:px-4">
            <div class="mb-3 d-inline-flex align-items-center justify-content-center"
                 style="width: 64px; height: 64px; border-radius: 50%; background-color: #f1f3f5;"
            >
                <b-icon icon="calendar-x" font-scale="2.4" style="color: #adb5bd;" />
            </div>
            <h5 class="mb-2">{{ $t('perfil_sga.renovacion_membresia_titulo', { simpatizante: textoSimpatizante }) }}</h5>
            <p class="text-muted mb-0 px-4">
                {{ $t('perfil_sga.sin_renovacion_membresia', { simpatizante: textoSimpatizante }) }}
            </p>
        </div>

        <!-- SELECTION STATE: Period picker and confirm -->
        <div v-if="paymentState === 'selection' && can_renew && status?.value !== 2" class="tw:py-2">
            <!-- No periods edge case -->
            <b-alert v-if="renewal_periods.length === 0" variant="warning" show>
                {{ $t('perfil_sga.sin_periodos') }}
            </b-alert>

            <!-- Period card -->
            <div v-if="selectedPeriod" class="tw:flex tw:items-center tw:p-5 tw:rounded-xl tw:text-white tw:mb-6 tw:shadow-lg tw:bg-gradient-to-br tw:from-indigo-500 tw:to-purple-600">
                <div class="tw:mr-4 tw:opacity-90">
                    <b-icon icon="calendar-check" font-scale="2" />
                </div>
                <div class="period-info">
                    <h4 class="tw:text-xl tw:font-bold tw:mb-1">{{ selectedPeriod.name }}</h4>
                    <p class="tw:text-sm tw:opacity-90 tw:mb-0">
                        <b-icon icon="calendar3" class="tw:mr-1" />
                        {{ formatDate(selectedPeriod.start_date) }} — {{ formatDate(selectedPeriod.end_date) }}
                    </p>
                </div>
            </div>

            <!-- Period selector (only if multiple) -->
            <b-form-group
                v-if="renewal_periods.length > 1"
                :label="$t('perfil_sga.seleccionar_periodo')"
                label-for="period-select"
                class="tw:mt-3"
            >
                <b-form-select
                    id="period-select"
                    v-model="selectedPeriodId"
                    :options="periodOptions"
                    :disabled="loading"
                />
            </b-form-group>

            <!-- Price breakdown: renovación de membresías sin promociones ni
                 descuentos (SGA no los aplica en este flujo). -->
            <div v-if="selectedPeriod" class="tw:bg-gray-100 tw:rounded-xl tw:p-5 tw:mb-6">
                <div class="tw:flex tw:justify-between tw:items-center tw:py-2 tw:border-b tw:border-dashed tw:border-gray-300">
                    <span class="tw:text-sm tw:text-gray-500">{{ $t('perfil_sga.precio_renovacion') }}</span>
                    <span class="tw:text-base tw:font-semibold tw:text-gray-800">{{ formattedPrice }}</span>
                </div>

                <div class="tw:flex tw:justify-between tw:items-center tw:pt-3 tw:mt-1 tw:border-t-2 tw:border-gray-300">
                    <span class="tw:text-sm tw:text-gray-500">{{ $t('perfil_sga.total') }}</span>
                    <span class="tw:text-2xl tw:font-extrabold tw:text-indigo-500">{{ formattedPrice }}</span>
                </div>
            </div>

        </div>

        <!-- SUCCESS STATE: Purchase completed -->
        <div v-if="paymentState === 'success'" class="tw:text-center tw:py-8 tw:px-4">
            <div class="tw:mb-6" style="animation: successPop 0.5s ease;">
                <b-icon icon="check-circle-fill" variant="success" font-scale="4" />
            </div>
            <h3 class="tw:text-2xl tw:font-bold tw:text-green-600 tw:mb-2">{{ $t('perfil_sga.renovacion_membresia_exitosa') }}</h3>
            <p class="tw:text-base tw:text-gray-500 tw:mb-8">{{ $t('perfil_sga.renovacion_membresia_gracias', { simpatizante: textoSimpatizante }) }}</p>

            <div v-if="successData" class="tw:bg-gray-100 tw:rounded-xl tw:p-6 tw:mb-8 tw:text-left">
                <!-- Order number -->
                <div class="tw:flex tw:justify-between tw:py-3 tw:border-b tw:border-gray-200">
                    <span class="tw:text-sm tw:text-gray-500">{{ $t('gracias.n_pedido') }}</span>
                    <span class="tw:font-semibold tw:text-gray-800">{{ successData.tpv_order }}</span>
                </div>

                <!-- Price breakdown: renovación de membresías sin descuentos. -->
                <div class="tw:mt-4 tw:pt-4 tw:border-t-2 tw:border-gray-300">
                    <div class="tw:flex tw:justify-between tw:py-3 tw:border-b tw:border-gray-200">
                        <span class="tw:text-sm tw:text-gray-500">{{ $t('perfil_sga.precio_renovacion') }}</span>
                        <span class="tw:font-semibold tw:text-gray-800">{{ formatPrice(successData.importe_total_sin_descuento || successData.importe_total) }}</span>
                    </div>

                    <!-- Price management charge -->
                    <div v-if="successData.price_management" class="tw:flex tw:justify-between tw:items-center tw:py-2 tw:text-sm">
                        <span class="tw:text-gray-600">
                            <b-icon icon="gear" class="tw:mr-1 tw:text-gray-400" />
                            {{ $t('perfil_sga.gastos_gestion') }}
                        </span>
                        <span class="tw:font-semibold tw:text-gray-800">+{{ formatPrice(successData.price_management) }}</span>
                    </div>

                    <div class="tw:flex tw:justify-between tw:py-3">
                        <span class="tw:text-sm tw:text-gray-500">{{ $t('perfil_sga.importe_total') }}</span>
                        <span class="tw:font-semibold tw:text-gray-800 tw:text-xl tw:text-indigo-500">{{ formatPrice(successData.importe_total) }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- PAYMENT STATE: Show Paycomet form or processing message -->
        <div v-if="paymentState === 'payment'">
            <b-alert v-if="paymentError" variant="danger" show>
                {{ paymentError }}
            </b-alert>

            <!-- Paycomet: Show form + 3D Secure iframe if needed -->
            <div v-if="!paymentProcessing">
                <!-- Payment cart summary -->
                <div class="tw:bg-gray-100 tw:rounded-xl tw:p-5 tw:mb-6">
                    <p class="tw:text-sm tw:font-semibold tw:text-gray-500 tw:uppercase tw:tracking-wider tw:mb-4">{{ $t('perfil_sga.resumen_pago') }}</p>
                    <div class="tw:flex tw:justify-between tw:items-center tw:py-1.5 tw:text-sm">
                        <span class="tw:text-gray-500">{{ $t('perfil_sga.precio_renovacion') }}</span>
                        <span class="tw:font-semibold tw:text-gray-800">{{ formatPrice(selectedPeriod?.price || 0) }}</span>
                    </div>
                    <div v-if="gastos_gestion > 0" class="tw:flex tw:justify-between tw:items-center tw:py-1.5 tw:text-sm tw:text-gray-500 tw:text-xs">
                        <span class="tw:text-gray-500">{{ $t('perfil_sga.gastos_gestion') }}</span>
                        <span class="tw:font-semibold tw:text-gray-800">+{{ formatPrice(gastos_gestion) }}</span>
                    </div>
                    <div class="tw:flex tw:justify-between tw:items-center tw:mt-3 tw:pt-3 tw:border-t-2 tw:border-gray-300">
                        <span class="tw:text-base tw:font-semibold tw:text-gray-800">{{ $t('perfil_sga.total_a_pagar') }}</span>
                        <span class="tw:text-xl tw:font-extrabold tw:text-indigo-500">{{ formattedTotalPrice }}</span>
                    </div>
                </div>

                <!-- Import FormularioPagoComponent with jet_id. El composable
                     envía session_id y tipo_venta='sga' al procesar el token:
                     el formulario debe recibir ambos para resolver el camino
                     de pago correcto (tarjeta/Bizum). Sin jet_id (respuesta
                     sin método de pago) NO se monta: el usuario ve el error y
                     solo puede cancelar (borra el carrito). -->
                <FormularioPagoComponent 
                    v-if="paymentData.jet_id"
                    :jet_id="paymentData.jet_id" 
                    :session_id="paymentData.session_id"
                    tipo_venta="sga"
                    @payToken="handlePayToken"
                />
            </div>

            <!-- 3D Secure challenge iframe -->
            <div v-else-if="paymentChallengeUrl">
                <p class="tw:text-center tw:mb-3">{{ $t('perfil_sga.procesando_pago') }}</p>
                <iframe 
                    class="tw:shadow tw:my-2 tw:bg-white" 
                    style="display: block; margin: 0 auto; overflow-y: scroll; overflow-x: hidden;" 
                    title="3D Secure"
                    sandbox="allow-top-navigation allow-scripts allow-same-origin allow-forms" 
                    :src="paymentChallengeUrl" 
                    width="325"
                    height="523" 
                    frameborder="0" 
                    scrolling="yes"
                    loading="lazy"
                ></iframe>
            </div>

            <!-- Payment processing spinner -->
            <div v-else class="tw:text-center">
                <b-spinner label="Procesando..."/>
                <p class="tw:mt-2">{{ $t('perfil_sga.procesando_pago') }}</p>
            </div>
        </div>

        <!-- Hidden div for Redsys form injection -->
        <div ref="redsysFormContainer" style="display: none;"></div>

        <!-- Modal Footer (footer slot) -->
        <template #footer>
            <!-- Blocked state: just close -->
            <template v-if="status?.value === 2">
                <b-button variant="primary" @click="closeModal">
                    {{ $t('perfil_sga.cerrar') }}
                </b-button>
            </template>

            <!-- No renewal state: just close -->
            <template v-else-if="!can_renew">
                <b-button variant="primary" @click="closeModal">
                    {{ $t('perfil_sga.cerrar') }}
                </b-button>
            </template>

            <!-- Selection state: cancel or confirm -->
            <template v-else-if="paymentState === 'selection'">
                <b-button variant="secondary" @click="closeModal" :disabled="loading" class="tw:mr-2">
                    {{ $t('formulario_pago.cancelar') }}
                </b-button>
                <b-button
                    variant="primary"
                    @click="confirmRenovacion"
                    :disabled="!canConfirm"
                >
                    <b-spinner v-if="loading" small class="tw:mr-1" />
                    {{ $t('perfil_sga.confirmar_renovacion') }}
                </b-button>
            </template>

            <!-- Payment state: cancel deletes cart and closes modal. Se permite
                 cancelar también durante el challenge 3DS (paymentProcessing
                 sigue true mientras el iframe está activo): cancelPayment
                 detiene el polling, cancela el autoenvío pendiente y borra el
                 carrito antes de cerrar. -->
            <template v-if="paymentState === 'payment'">
                <b-button
                    variant="secondary"
                    @click="onCancelClick"
                    :disabled="loading"
                    class="tw:mr-2"
                >
                    <b-spinner v-if="loading" small class="tw:mr-1" />
                    {{ $t('formulario_pago.cancelar') }}
                </b-button>
            </template>

            <!-- Success state: close modal -->
            <template v-if="paymentState === 'success'">
                <b-button
                    variant="primary"
                    @click="closeModal"
                >
                    {{ $t('perfil_sga.cerrar') }}
                </b-button>
            </template>
        </template>
    </ModalComponent>
</template>

<script>
    import { ref, computed, getCurrentInstance, onMounted, onUnmounted } from 'vue';
    import ModalComponent from '@/components/backend/components/ModalComponent';
    import FormularioPagoComponent from '@/components/frontend/compra/FormularioPagoComponent.vue';
    import useSgaRenovacionMembresiaPago from '@/composables/front/sga/useSgaRenovacionMembresiaPago';

    export default {
        name: 'RenovacionMembresiaSgaModal',

        components: {
            ModalComponent,
            FormularioPagoComponent
        },

        props: {
            abonado_id: {
                type: [String, Number],
                required: true
            },
            can_renew: {
                type: Boolean,
                default: true
            },
            renewal_periods: {
                type: Array,
                default: () => []
            },
            sales_category_id: {
                type: String,
                default: null
            },
            gastos_gestion: {
                type: Number,
                default: 0
            },
            status: {
                type: Object,
                default: null
            }
        },

        setup(props, { emit }) {
            const instance = getCurrentInstance();

            // Local refs shared between composables
            const selectedPeriodId = ref(null);

            // Computed period object
            const selectedPeriod = computed(() => {
                if (!selectedPeriodId.value) return null;
                return props.renewal_periods.find(p => p.id === selectedPeriodId.value);
            });
            // Label of the membership type, taken from the theater config
            // (texto_simpatizante) instead of hardcoding "membresía".
            const textoSimpatizante = computed(() => {
                const teatro = instance.proxy.$store.getters.getTeatro || {};
                return teatro.texto_simpatizante || '';
            });

            // Price formatter function
            const formatPrice = price => new Intl.NumberFormat('es-ES', {
                style: 'currency',
                currency: 'EUR'
            }).format(price);

            // Context for payment composable
            const ctx = {
                $store: instance.proxy.$store,
                $router: instance.proxy.$router,
                $toast: instance.proxy.$toast,
                $t: instance.proxy.$t.bind(instance.proxy),
                $config: instance.proxy.config,
                $emit: emit,
                getCookie: name => instance.proxy.$cookies.get(name)
            };

            // Payment composable: renovación de membresías sin promociones ni
            // descuentos (SGA no los aplica en este flujo).
            const pago = useSgaRenovacionMembresiaPago(ctx, {
                selectedPeriod,
                abonado_id: props.abonado_id,
                sales_category_id: props.sales_category_id,
                gastos_gestion: props.gastos_gestion,
                refs: instance.proxy.$refs
            });

            // Period options computed
            const periodOptions = computed(() => {
                return props.renewal_periods.map(period => ({
                    value: period.id,
                    text: `${period.name} (${formatPrice(period.price)})`
                }));
            });

            // Formatted base price (reuses formatPrice helper)
            const formattedPrice = computed(() => {
                if (!selectedPeriod.value) return '';
                return formatPrice(selectedPeriod.value.price);
            });

            // Formatted total price for payment state (reuses formatPrice helper).
            // Un total de 0 (bonificación total) es un valor de negocio válido:
            // se comprueba != null, no truthy, para no mostrar un importe vacío.
            const formattedTotalPrice = computed(() => {
                if (pago.paymentData.value.total_price == null) return '';
                return formatPrice(pago.paymentData.value.total_price);
            });

            // Guard for confirm button
            const canConfirm = computed(() => {
                return (
                    !pago.loading.value &&
                    selectedPeriodId.value !== null &&
                    props.renewal_periods.length > 0
                );
            });

            // Close modal helper. Si el pago sigue pendiente (carrito creado),
            // lo cancelamos en backend antes de cerrar: si no, el carrito de
            // renovación queda activo y sin visibilidad para el usuario. El
            // cierre en estado success NO cancela nada.
            const closeModal = () => {
                if (pago.paymentState.value === 'payment' && pago.paymentData.value.cart_id) {
                    pago.cancelPayment().catch(() => {
                        // El error ya está en paymentError; el modal sigue
                        // abierto para que el usuario pueda reintentar.
                    });
                    return;
                }
                emit('close');
            };

            // Botón Cancelar del footer: mismo flujo que closeModal pero sin
            // duplicar la lógica (cancelPayment relanza si el DELETE falla).
            const onCancelClick = () => {
                pago.cancelPayment().catch(() => {
                    // El error ya está en paymentError; el modal sigue abierto.
                });
            };

            // Cierre externo (ESC/clic exterior/X): vue-js-modal emite
            // `closed` sin pasar por closeModal(). Si hay carrito pendiente,
            // se cancela; si el borrado falla, se reabre el modal para que el
            // usuario vea el error y pueda reintentar. Sin carrito pendiente,
            // se propaga el cierre al padre (desmonta y limpia el estado).
            const onModalClosed = () => {
                if (pago.paymentState.value === 'payment' && pago.paymentData.value.cart_id) {
                    pago.cancelPayment().catch(() => {
                        instance.proxy.$modal.show('renovacion-membresia-sga-modal');
                    });
                } else {
                    emit('close');
                }
            };

            // Format date helper
            const formatDate = dateString => {
                const date = new Date(dateString);
                return date.toLocaleDateString('es-ES');
            };

            // Lifecycle: auto-select single period
            onMounted(() => {
                if (props.renewal_periods.length === 1) {
                    selectedPeriodId.value = props.renewal_periods[0].id;
                }
            });

            // Lifecycle: cierre por ESC/clic exterior no pasa por closeModal().
            // cancelOnUnmount detiene el polling y el autoenvío pendiente y
            // borra el carrito si el pago sigue pendiente (sin emitir close:
            // el modal ya se desmonta).
            onUnmounted(() => {
                pago.cancelOnUnmount();
            });

            return {
                // Selection state
                selectedPeriodId,
                selectedPeriod,
                periodOptions,
                formattedPrice,
                formattedTotalPrice,
                canConfirm,
                formatPrice,
                closeModal,
                onCancelClick,
                onModalClosed,
                formatDate,
                // Label of the membership type from the theater config
                textoSimpatizante,

                // Payment composable
                ...pago
            };
        }
    };
</script>

<style>
@keyframes successPop {
    0% { transform: scale(0); opacity: 0; }
    70% { transform: scale(1.2); }
    100% { transform: scale(1); opacity: 1; }
}
</style>
